import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

// Relative-time helper — keeps the CRM feeling alive without leaking
// backend timestamps directly to the UI.
const relTime = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

// Human labels for project / proposal statuses (kept close to the CRM
// language the user already sees on the Project workspace).
const PROJECT_STATUS_LABEL = {
  draft: "Draft",
  sent: "Proposal sent",
  accepted: "Accepted",
  declined: "Declined",
  archived: "Archived",
};

const PROPOSAL_STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/clients"),
      api.get("/projects").catch(() => ({ data: [] })),
    ]);
    setClients(c.data);
    setProjects(p.data || []);
  };
  useEffect(() => { load(); }, []);

  // Aggregate project + proposal signal per client so the CRM row shows
  // meaningful state rather than static contact fields.
  const summaryByClient = useMemo(() => {
    const map = {};
    for (const p of projects) {
      if (!p.client_id) continue;
      const s = map[p.client_id] || (map[p.client_id] = { count: 0, latest: null });
      s.count += 1;
      const cand = { updated_at: p.updated_at, status: p.status, title: p.title, delivery_stage: p.delivery_stage };
      if (!s.latest || new Date(cand.updated_at || 0) > new Date(s.latest.updated_at || 0)) s.latest = cand;
    }
    return map;
  }, [projects]);

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/clients/${editing.id}`, data);
      else await api.post("/clients", data);
      toast.success(editing?.id ? "Client updated" : "Client added");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted");
    load();
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-7xl" data-testid="clients-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline">Studio · Contacts</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Clients</h1>
          <p className="text-muted-foreground mt-3">Everyone you've quoted or built for, with a glance at what's happening on their project.</p>
        </div>
        <button data-testid="btn-add-client" onClick={() => { setEditing({}); setOpen(true); }} className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5}/> Add client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <div className="font-serif text-2xl mb-2">No clients yet</div>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Add your first client to begin building projects, proposals and portal invites for them.</p>
          <button data-testid="btn-add-first-client" onClick={() => { setEditing({}); setOpen(true); }} className="btn-accent mx-auto">
            <Plus className="w-4 h-4" strokeWidth={1.5}/> Add client
          </button>
        </div>
      ) : (
        <div className="luxe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: "#F4F2EC" }}>
                <th className="text-left px-6 py-4 overline text-[10px]">Name</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Contact</th>
                <th className="text-left px-6 py-4 overline text-[10px]">Projects</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden lg:table-cell">Project status</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden lg:table-cell">Proposal</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Last activity</th>
                <th className="text-right px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const s = summaryByClient[c.id] || { count: 0, latest: null };
                const projectStage = s.latest?.delivery_stage;
                const proposalStatus = s.latest ? (PROPOSAL_STATUS_LABEL[s.latest.status] || PROJECT_STATUS_LABEL[s.latest.status] || "—") : "—";
                return (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-[color:hsl(var(--muted))]/40">
                    <td className="px-6 py-5">
                      <div className="font-medium">{c.name}</div>
                      <div className="md:hidden text-xs text-muted-foreground mt-1">{c.email || c.phone || "—"}</div>
                    </td>
                    <td className="px-6 py-5 hidden md:table-cell text-muted-foreground">
                      <div className="truncate max-w-[220px]">{c.email || "—"}</div>
                      {c.phone && <div className="text-xs">{c.phone}</div>}
                    </td>
                    <td className="px-6 py-5" data-testid={`client-projects-${c.id}`}>
                      {s.count > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs bg-foreground text-background rounded-sm">{s.count}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-6 py-5 hidden lg:table-cell text-muted-foreground text-xs" data-testid={`client-project-status-${c.id}`}>
                      {projectStage || "—"}
                    </td>
                    <td className="px-6 py-5 hidden lg:table-cell" data-testid={`client-proposal-status-${c.id}`}>
                      {s.latest ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-muted">{proposalStatus}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-6 py-5 hidden md:table-cell text-muted-foreground text-xs" data-testid={`client-activity-${c.id}`}>
                      {relTime(s.latest?.updated_at)}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`btn-edit-client-${c.id}`} onClick={() => { setEditing(c); setOpen(true); }} className="p-2 hover:bg-muted"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                        <button data-testid={`btn-delete-client-${c.id}`} onClick={() => remove(c.id)} className="p-2 hover:bg-muted text-[color:hsl(var(--destructive))]"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && <ClientForm client={editing} onClose={() => { setOpen(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

function ClientForm({ client, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "", ...client });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="client-form-modal">
      <div className="bg-card max-w-lg w-full p-8 border border-border">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline">{client?.id ? "Edit" : "New"}</div>
            <h3 className="font-serif font-light text-3xl mt-2">{client?.id ? "Edit client" : "Add client"}</h3>
          </div>
          <button data-testid="btn-close-client-form" onClick={onClose}><X strokeWidth={1.5}/></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Full name *</label>
            <input data-testid="input-client-name" className="luxe-input" value={form.name} onChange={set("name")}/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Email</label>
              <input data-testid="input-client-email" className="luxe-input" value={form.email} onChange={set("email")}/>
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Phone</label>
              <input data-testid="input-client-phone" className="luxe-input" value={form.phone} onChange={set("phone")}/>
            </div>
          </div>
          <div>
            <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Address</label>
            <input data-testid="input-client-address" className="luxe-input" value={form.address} onChange={set("address")}/>
          </div>
          <div>
            <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Notes</label>
            <textarea data-testid="input-client-notes" rows="3" className="luxe-input" value={form.notes} onChange={set("notes")}/>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button data-testid="btn-save-client" onClick={() => onSave(form)} className="btn-primary">Save client</button>
        </div>
      </div>
    </div>
  );
}
